import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { BONUS_STREAK_CONFIG } from '@/src/constants/bonusStreakConfig';
import type { BonusStreakState } from '@/src/types/bonusStreak';
import type { EvidenceFind } from '../../types/scoring';
import { calculateIPScore } from '../../utils/ipScoringSystem';

interface ScoringState {
  evidenceFinds: EvidenceFind[];
  currentIPScore: number;
  lastClueTimestamp: number;
  bonusStreak: BonusStreakState | null;
  highestSpeedStreak: number;
  firstFindTimestamp: number | null;
  lastFindTimestamp: number | null;
  totalCompletionTime: number;
}

const initialState: ScoringState = {
  evidenceFinds: [],
  currentIPScore: 0,
  lastClueTimestamp: 0,
  bonusStreak: null,
  highestSpeedStreak: 0,
  firstFindTimestamp: null,
  lastFindTimestamp: null,
  totalCompletionTime: 0,
};

export const scoringSlice = createSlice({
  name: 'scoring',
  initialState,
  reducers: {
    resetScoring: () => initialState,

    // Initialize lastClueTimestamp to game start time so first find timing is accurate
    initializeGameStartTime: (state, action: PayloadAction<number>) => ({
      ...state,
      lastClueTimestamp: action.payload,
    }),

    resetBonusStreak: (state) => ({
      ...state,
      bonusStreak: null,
      highestSpeedStreak: 0,
    }),

    addEvidenceFind: (
      state,
      action: PayloadAction<{
        itemId: string;
        timeTakenSeconds: number;
        baseTimeEfficiencyBonus: number;
      }>,
    ) => {
      const { itemId, timeTakenSeconds, baseTimeEfficiencyBonus } =
        action.payload;
      const timeWhenFound = Date.now();

      const evidenceFind: EvidenceFind = {
        itemId,
        timeWhenFound,
        timeTakenSeconds,
        baseTimeEfficiencyBonus,
      };

      const newFirstFindTimestamp =
        state.firstFindTimestamp === null
          ? timeWhenFound
          : state.firstFindTimestamp;

      // Calculate completion time in seconds (timestamps are in ms from Date.now())
      const newTotalCompletionTime =
        newFirstFindTimestamp !== null
          ? (timeWhenFound - newFirstFindTimestamp) / 1000
          : 0;

      return {
        ...state,
        evidenceFinds: [...state.evidenceFinds, evidenceFind],
        lastClueTimestamp: timeWhenFound,
        firstFindTimestamp: newFirstFindTimestamp,
        lastFindTimestamp: timeWhenFound,
        totalCompletionTime: newTotalCompletionTime,
      };
    },

    setCurrentIPScore: (
      state,
      action: PayloadAction<{
        wrongClickTimestamps: number[];
        requiredCount: number;
        caseId: string;
        streakTimeThreshold: number;
      }>,
    ) => {
      const {
        wrongClickTimestamps,
        requiredCount,
        caseId,
        streakTimeThreshold,
      } = action.payload;

      const ipScore = calculateIPScore(
        state.evidenceFinds,
        wrongClickTimestamps,
        requiredCount,
        caseId,
        streakTimeThreshold,
      );

      return {
        ...state,
        currentIPScore: ipScore.finalScore,
      };
    },

    setLastClueTimestamp: (state, action: PayloadAction<number>) => ({
      ...state,
      lastClueTimestamp: action.payload,
    }),

    startBonusStreak: (
      state,
      action: PayloadAction<{ baseTimeEfficiencyBonus: number }>,
    ) => {
      const { baseTimeEfficiencyBonus: _baseTimeEfficiencyBonus } =
        action.payload;
      const currentTime = Date.now();

      return {
        ...state,
        bonusStreak: {
          isActive: true,
          isTimerRunning: true,
          consecutiveBonuses: 1,
          currentMultiplier: 1,
          streakStartTime: currentTime,
          lastFindTime: currentTime,
          timeRemaining: BONUS_STREAK_CONFIG.STREAK_TIMEOUT,
          maxTime: BONUS_STREAK_CONFIG.STREAK_TIMEOUT,
        },
        // Update highest speed streak (consecutive bonuses + 1 for initial fast find)
        highestSpeedStreak: Math.max(state.highestSpeedStreak, 2),
      };
    },

    continueBonusStreak: (
      state,
      _action: PayloadAction<{ baseTimeEfficiencyBonus: number }>,
    ) => {
      if (!state.bonusStreak || !state.bonusStreak.isActive) return state;

      const currentTime = Date.now();
      const newConsecutive = state.bonusStreak.consecutiveBonuses + 1;
      const newMultiplier = Math.min(5, newConsecutive); // Cap at 5

      // Update highest speed streak
      const streakLength = newConsecutive + 1;

      return {
        ...state,
        bonusStreak: {
          ...state.bonusStreak,
          isTimerRunning: true, // Keep timer running
          consecutiveBonuses: newConsecutive,
          currentMultiplier: newMultiplier,
          lastFindTime: currentTime,
          timeRemaining: BONUS_STREAK_CONFIG.STREAK_TIMEOUT, // Reset to full time
        },
        highestSpeedStreak: Math.max(state.highestSpeedStreak, streakLength),
      };
    },

    updateStreakTimer: (
      state,
      action: PayloadAction<{ timeRemaining: number }>,
    ) => {
      if (!state.bonusStreak) return state;

      const { timeRemaining } = action.payload;

      if (timeRemaining <= 0) {
        return {
          ...state,
          bonusStreak: {
            ...state.bonusStreak,
            isActive: false,
            isTimerRunning: false,
            timeRemaining: 0,
          },
        };
      } else {
        return {
          ...state,
          bonusStreak: {
            ...state.bonusStreak,
            timeRemaining,
          },
        };
      }
    },

    breakBonusStreak: (state) => {
      if (state.bonusStreak) {
        return {
          ...state,
          bonusStreak: {
            ...state.bonusStreak,
            isActive: false,
            isTimerRunning: false,
          },
        };
      }
      return state;
    },

    stopStreakTimer: (state) => {
      if (state.bonusStreak) {
        return {
          ...state,
          bonusStreak: {
            ...state.bonusStreak,
            isTimerRunning: false,
          },
        };
      }
      return state;
    },

    clearBonusStreak: (state) => ({
      ...state,
      bonusStreak: null,
    }),

    setHighestSpeedStreak: (state, action: PayloadAction<number>) => ({
      ...state,
      highestSpeedStreak: Math.max(state.highestSpeedStreak, action.payload),
    }),
  },
});

export const {
  resetScoring,
  initializeGameStartTime,
  resetBonusStreak,
  addEvidenceFind,
  setCurrentIPScore,
  setLastClueTimestamp,
  startBonusStreak,
  continueBonusStreak,
  updateStreakTimer,
  breakBonusStreak,
  clearBonusStreak,
  setHighestSpeedStreak,
} = scoringSlice.actions;

export default scoringSlice.reducer;
